// src/components/News.js
import React, { useEffect, useState, useContext } from 'react';
import { Container } from '@mui/material';
import { AuthContext } from '../utils/AuthContext';
import { useNewsNotifications } from '../utils/NewsNotificationContext';
import { getNewsItems, updateUserLastNewsVisit } from '../utils/firestoreUtils';

// Render block content — headings, body text, images
function NewsBlock({ block }) {
  switch (block.type) {
    case 'heading':
      return (
        <h2 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.8rem',
          letterSpacing: '0.03em',
          color: 'var(--text-primary)',
          fontWeight: 400,
          margin: '28px 0 10px',
          lineHeight: 1.1,
        }}>
          {block.content}
        </h2>
      );
    case 'subheading':
      return (
        <h3 style={{
          fontFamily: 'var(--font-body)',
          fontSize: '1rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          margin: '20px 0 8px',
          letterSpacing: '0.01em',
        }}>
          {block.content}
        </h3>
      );
    case 'text':
      return (
        <p style={{
          fontSize: '0.95rem',
          color: 'var(--text-secondary)',
          lineHeight: 1.8,
          margin: '0 0 14px',
          whiteSpace: 'pre-line',
        }}>
          {block.content}
        </p>
      );
    case 'image':
      return (
        <div style={{ margin: '16px 0' }}>
          <img
            src={block.url}
            alt={block.caption || ''}
            style={{
              width: '100%',
              borderRadius: 'var(--radius-lg)',
              display: 'block',
              boxShadow: 'var(--shadow-md)',
            }}
          />
          {block.caption && (
            <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', textAlign: 'center', marginTop: 8, fontStyle: 'italic' }}>
              {block.caption}
            </div>
          )}
        </div>
      );
    default:
      return null;
  }
}

function News() {
  const { user } = useContext(AuthContext);
  const { clearNotification } = useNewsNotifications();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    getNewsItems().then(data => { setItems(data); setLoading(false); });
    if (user) { updateUserLastNewsVisit(user.uid); clearNotification(); }
  }, [user]);

  const fmtDate = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <div style={{ padding: '40px 0' }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ marginBottom: 24 }}>
              <div className="skeleton" style={{ height: 24, width: '40%', borderRadius: 6, marginBottom: 10 }} />
              <div className="skeleton" style={{ height: 16, width: '70%', borderRadius: 6, marginBottom: 6 }} />
              <div className="skeleton" style={{ height: 16, width: '55%', borderRadius: 6 }} />
            </div>
          ))}
        </div>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ pb: 8 }}>
      <div className="page-header">
        <h1 className="page-title">News</h1>
        <p className="page-subtitle">Updates from the Model 3 Challenge</p>
      </div>

      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-tertiary)' }}>
          No news posts yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {items.map((item, idx) => {
            const isOpen = expanded === item.id;
            const blocks = item.blocks || [];
            // First image block for the hero
            const heroImg = blocks.find(b => b.type === 'image');
            // First text block for preview
            const previewText = blocks.find(b => b.type === 'text')?.content || '';
            const preview = previewText.length > 160 ? previewText.slice(0, 160) + '…' : previewText;

            return (
              <article key={item.id} style={{
                borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
                padding: '28px 0',
              }}>
                {/* Hero image */}
                {heroImg && !isOpen && (
                  <img
                    src={heroImg.url}
                    alt=""
                    style={{
                      width: '100%',
                      height: 220,
                      objectFit: 'cover',
                      borderRadius: 'var(--radius-lg)',
                      marginBottom: 16,
                      display: 'block',
                      cursor: 'pointer',
                    }}
                    onClick={() => setExpanded(isOpen ? null : item.id)}
                  />
                )}

                {/* Date + category tag */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{
                    fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'var(--accent)',
                  }}>
                    {fmtDate(item.date)}
                  </span>
                  {item.tag && (
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.07em',
                      textTransform: 'uppercase', color: 'var(--text-tertiary)',
                      background: 'var(--surface-2)', padding: '2px 8px',
                      borderRadius: 'var(--radius-pill)',
                    }}>
                      {item.tag}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h2
                  onClick={() => setExpanded(isOpen ? null : item.id)}
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                    letterSpacing: '0.02em',
                    fontWeight: 400,
                    color: 'var(--text-primary)',
                    lineHeight: 1.1,
                    margin: '0 0 10px',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-primary)'}
                >
                  {item.title}
                </h2>

                {/* Preview or full content */}
                {isOpen ? (
                  <div style={{ marginTop: 8 }}>
                    {blocks.map((block, bi) => <NewsBlock key={bi} block={block} />)}
                    <button
                      onClick={() => setExpanded(null)}
                      style={{
                        marginTop: 16, background: 'none', border: 'none',
                        color: 'var(--accent)', fontFamily: 'var(--font-body)',
                        fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', padding: 0,
                      }}
                    >
                      ↑ Collapse
                    </button>
                  </div>
                ) : (
                  <>
                    {preview && (
                      <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 12px' }}>
                        {preview}
                      </p>
                    )}
                    <button
                      onClick={() => setExpanded(item.id)}
                      style={{
                        background: 'none', border: 'none',
                        color: 'var(--accent)', fontFamily: 'var(--font-body)',
                        fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', padding: 0,
                      }}
                    >
                      Read more →
                    </button>
                  </>
                )}
              </article>
            );
          })}
        </div>
      )}
    </Container>
  );
}

export default News;
